// API client for the venue touchscreens: the crit schedule (Staff) and the
// screens + published bundles (Admin). Wire format is the portal's snake_case.
import { api } from './api';

export type SessionKind = 'entry' | 'break' | 'event';

export interface CritRoom {
  id: string;
  name: string;
  sort_order: number;
}

export interface CritHeading {
  id: number;
  date: string;
  room: string;
  title: string;
  sponsor: string | null;
  sort_order: number;
  source: 'import' | 'manual';
}

export interface CritSession {
  id: number;
  date: string;
  room: string;
  start: string;
  end: string;
  kind: SessionKind;
  nomination_code: string | null;
  nomination_uuid: string | null;
  project_title: string | null;
  architect: string | null;
  title: string | null;
  subtitle: string | null;
  heading: string | null;
  source: 'import' | 'manual';
  /** Set when an entry slot's project number doesn't match an entry. */
  problem: string | null;
}

export interface CritSchedule {
  dates: string[];
  rooms: CritRoom[];
  headings: CritHeading[];
  sessions: CritSession[];
}

export interface CritImportSummary {
  days: Array<{ date: string; rooms: number; entries: number; breaks: number; events: number }>;
  sessions: number;
  headings: number;
  unmatched_codes: string[];
  warnings: string[];
  replaced_sessions: number;
  kept_manual_sessions: number;
}

export interface CritSessionInput {
  date: string;
  room: string;
  start: string;
  end: string;
  kind: SessionKind;
  nomination_code?: string | null;
  title?: string | null;
  subtitle?: string | null;
  heading?: string | null;
}

export async function getCritSchedule(): Promise<CritSchedule> {
  return (await api.get<CritSchedule>('/crit-schedule')).data;
}

export async function importCritSchedule(file: File, weekOf: string, commit: boolean): Promise<CritImportSummary> {
  const form = new FormData();
  form.append('file', file);
  form.append('week_of', weekOf);
  form.append('commit', String(commit));
  return (await api.post<CritImportSummary>('/crit-schedule/import', form, { timeout: 120000 })).data;
}

export async function createCritSession(input: CritSessionInput): Promise<CritSession> {
  return (await api.post<CritSession>('/crit-schedule/sessions', input)).data;
}

export async function updateCritSession(id: number, input: CritSessionInput): Promise<CritSession> {
  return (await api.put<CritSession>(`/crit-schedule/sessions/${id}`, input)).data;
}

export async function deleteCritSession(id: number): Promise<void> {
  await api.delete(`/crit-schedule/sessions/${id}`);
}

export async function saveCritHeading(
  id: number | null,
  input: { date: string; room: string; title: string; sponsor: string | null; sort_order: number }
): Promise<CritHeading> {
  const res = id == null ? await api.post<CritHeading>('/crit-schedule/headings', input) : await api.put<CritHeading>(`/crit-schedule/headings/${id}`, input);
  return res.data;
}

export async function deleteCritHeading(id: number): Promise<void> {
  await api.delete(`/crit-schedule/headings/${id}`);
}

export async function saveCritRoom(room: CritRoom): Promise<CritRoom> {
  return (await api.put<CritRoom>(`/crit-schedule/rooms/${encodeURIComponent(room.id)}`, room)).data;
}

// ---- Screens + bundles (Admin) ----

export type ScreenMode = 'room' | 'picker' | 'browse';

export interface KioskScreen {
  id: string;
  name: string;
  mode: ScreenMode;
  rooms: string[];
  day_override: string | null;
  idle_seconds: number;
  enrolled: boolean;
  enrolled_at: string | null;
  enrolment_pending: boolean;
  last_seen_at: string | null;
  online: boolean;
  last_ip: string | null;
  hostname: string | null;
  agent_version: string | null;
  bundle_version: number | null;
  bundle_source: string | null;
  sync_state: string | null;
  sync_progress: string | null;
  last_error: string | null;
  uptime_seconds: number | null;
  reload_seq: number;
  created_at: string;
}

export interface KioskScreenInput {
  name: string;
  mode: ScreenMode;
  rooms: string[];
  day_override: string | null;
  idle_seconds: number;
}

export interface KioskBundle {
  version: number;
  status: 'building' | 'ready' | 'failed';
  year: string;
  entry_count: number;
  session_count: number;
  file_count: number;
  total_bytes: number;
  warnings: string[];
  error: string | null;
  actor: string | null;
  created_at: string;
  completed_at: string | null;
}

export async function listScreens(): Promise<KioskScreen[]> {
  return (await api.get<KioskScreen[]>('/kiosk/screens')).data;
}

export async function createScreen(input: KioskScreenInput): Promise<KioskScreen> {
  return (await api.post<KioskScreen>('/kiosk/screens', input)).data;
}

export async function updateScreen(id: string, input: KioskScreenInput): Promise<KioskScreen> {
  return (await api.put<KioskScreen>(`/kiosk/screens/${id}`, input)).data;
}

export async function deleteScreen(id: string): Promise<void> {
  await api.delete(`/kiosk/screens/${id}`);
}

export async function issueEnrolmentCode(id: string): Promise<{ code: string; expires_at: string }> {
  return (await api.post<{ code: string; expires_at: string }>(`/kiosk/screens/${id}/enrolment-code`)).data;
}

export async function reloadScreen(id: string | null): Promise<void> {
  await api.post(id ? `/kiosk/screens/${id}/reload` : '/kiosk/screens/reload-all');
}

export async function listBundles(): Promise<KioskBundle[]> {
  return (await api.get<KioskBundle[]>('/kiosk/bundles')).data;
}

export async function publishBundle(year: string): Promise<KioskBundle> {
  return (await api.post<KioskBundle>('/kiosk/bundles', { year })).data;
}

/** Native browser download of the USB zip (can be several GB — never buffered in JS). */
export async function downloadUsbBundle(version: number): Promise<void> {
  const res = await api.post<{ token: string }>(`/kiosk/bundles/${version}/usb-token`);
  const a = document.createElement('a');
  a.href = `/api/kiosk/bundles/usb/${encodeURIComponent(res.data.token)}`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function describeApiError(e: unknown, fallback: string): string {
  const err = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
  const messages: Record<string, string> = {
    already_building: 'A bundle is already being built — wait for it to finish.',
    room_required: 'Pick a room for a single-room screen.',
    name_required: 'Give the screen a name.',
    invalid_kind: 'Pick what kind of slot this is.',
    end_before_start: 'The end time must be after the start time.',
    nomination_code_required: 'Enter the project number for an entry slot.',
    title_required: 'Give this slot a title.',
    unreadable_workbook: "That file couldn't be read — is it the .xlsx crit schedule?",
    invalid_week_of: 'Pick a date in festival week.',
  };
  return (err?.error && messages[err.error]) || err?.error || fallback;
}
